import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalFonts,
  signalRadii,
  signalShadows,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  LogoMark,
  Mono,
  ScreenFrame,
  SignalButton,
  StatusPill,
} from '@/common/components/signal';

/** The two builds that aren't the visitor's platform, offered as plain rows. */
const OTHER_PLATFORMS: [string, string, string][] = [
  ['WIN', 'Windows', '151 MB'],
  ['LIN', 'Linux · AppImage', '156 MB'],
];

const FACTS: [string, React.ReactNode][] = [
  ['REQUIRES', 'macOS 13+, Windows 10+, Ubuntu 22.04+'],
  ['AUTO-UPDATES', 'Ships on the stable channel'],
  ['RELEASE NOTES', null],
];

/** Password meter: three of four segments filled. */
const STRENGTH = [true, true, true, false];

/** Accent inline link, used in the fine print and release notes. */
const InlineLink: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="a"
    href="#"
    sx={{
      color: signalColors.accentBright,
      fontWeight: 600,
      textDecoration: 'none',
    }}
  >
    {children}
  </Box>
);

/** Mono field label above an input. */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Mono component="label" size={10} weight={600} letterSpacing="0.12em">
    {children}
  </Mono>
);

/** Read-only input mock. `placeholder` renders the muted, unfilled variant. */
const Field: React.FC<{
  label: string;
  value: string;
  placeholder?: boolean;
  sx?: object;
}> = ({ label, value, placeholder = false, sx }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', ...sx }}>
    <FieldLabel>{label}</FieldLabel>
    <Box
      sx={{
        background: signalSurfaces.fill,
        border: `1px solid ${signalBorders.stronger}`,
        borderRadius: `${signalRadii.md}px`,
        padding: '11px 14px',
        fontSize: '14px',
        color: placeholder ? signalText.muted : signalText.primary,
      }}
    >
      {value}
    </Box>
  </Box>
);

/**
 * 15 — Download & register.
 *
 * The two halves of getting started, shown together because they're the same
 * decision: the agent is what makes sync possible, and the account is what the
 * agent uploads into. The download side leads with the platform it detected and
 * demotes the other two to rows rather than hiding them behind a dropdown —
 * one fewer click for the minority without costing the majority anything.
 */
export const Screen15Register: React.FC = () => (
  <ScreenFrame
    index="15"
    title="Download and register"
    note="DESKTOP AGENT · SIGN UP"
    bare
  >
    <Box
      sx={{
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap',
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          width: 700,
          maxWidth: '100%',
          borderRadius: `${signalRadii['3xl']}px`,
          border: `1px solid ${signalBorders.strong}`,
          background: signalSurfaces.screen,
          boxShadow: '0 40px 90px -40px rgba(0,0,0,0.95)',
          padding: '38px 36px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '9px',
            marginBottom: '26px',
          }}
        >
          <LogoMark showWordmark={false} size={26} />
          <Box
            component="span"
            sx={{
              fontFamily: signalFonts.display,
              fontWeight: 700,
              fontSize: '15px',
              letterSpacing: '-0.02em',
            }}
          >
            StudioVault Desktop
          </Box>
          <StatusPill size={9.5} sx={{ marginLeft: '10px' }}>
            v2.5.0 · STABLE
          </StatusPill>
        </Box>

        <Box
          component="h3"
          sx={{
            margin: '0 0 10px',
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '38px',
            letterSpacing: '-0.035em',
            lineHeight: 1.02,
            maxWidth: '20ch',
          }}
        >
          The agent that does the heavy lifting.
        </Box>
        <Box
          component="p"
          sx={{
            margin: '0 0 26px',
            fontSize: '14px',
            lineHeight: 1.6,
            color: signalText.secondary,
            maxWidth: '52ch',
          }}
        >
          Watches your session folders, hashes only what changed, and uploads in
          parallel. Required for sync, commits and locating projects.
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            marginBottom: '14px',
          }}
        >
          <SignalButton
            size="xl"
            sx={{
              padding: '13px 22px',
              borderRadius: '11px',
              boxShadow: signalShadows.accentXl,
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
            }}
          >
            ↓ Download for macOS
          </SignalButton>
          <Mono size={11}>APPLE SILICON · 148 MB</Mono>
        </Box>

        <Box sx={{ display: 'flex', gap: '10px', marginBottom: '26px' }}>
          {OTHER_PLATFORMS.map(([code, name, size]) => (
            <Box
              key={code}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '9px',
                border: `1px solid ${signalBorders.strong}`,
                borderRadius: '11px',
                padding: '11px 15px',
                fontSize: '13px',
                color: signalText.secondary,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.26)',
                  color: signalText.primary,
                },
              }}
            >
              <Mono size={10} weight={600}>
                {code}
              </Mono>
              {name}
              <Mono size={10}>{size}</Mono>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            paddingTop: '24px',
            borderTop: `1px solid ${signalBorders.subtle}`,
          }}
        >
          {FACTS.map(([label, body]) => (
            <Box key={label}>
              <Mono
                size={10}
                weight={600}
                sx={{ display: 'block', marginBottom: '7px' }}
              >
                {label}
              </Mono>
              <Box
                sx={{
                  fontSize: '12.5px',
                  color: signalText.secondary,
                  lineHeight: 1.55,
                }}
              >
                {body ?? <InlineLink>What&apos;s new in 2.5.0</InlineLink>}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          width: 452,
          maxWidth: '100%',
          borderRadius: `${signalRadii['3xl']}px`,
          border: `1px solid ${signalBorders.medium}`,
          background: signalSurfaces.panel,
          boxShadow: '0 40px 90px -40px rgba(0,0,0,0.95)',
          padding: '34px 32px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LogoMark size={26} sx={{ marginBottom: '28px' }} />

        <Box
          component="h3"
          sx={{
            margin: '0 0 6px',
            fontFamily: signalFonts.display,
            fontWeight: 800,
            fontSize: '30px',
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
          }}
        >
          Start your vault.
        </Box>
        <Box
          component="p"
          sx={{
            margin: '0 0 24px',
            fontSize: '13px',
            color: signalText.secondary,
            lineHeight: 1.6,
          }}
        >
          Free forever for 500 MB. No card needed.
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <Field label="NAME" value="Mara Kade" sx={{ flex: 1 }} />
            <Field
              label="ARTIST / STUDIO"
              value="Optional"
              placeholder
              sx={{ flex: 1 }}
            />
          </Box>
          <Field label="EMAIL" value="mara@autocannon.fm" />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <FieldLabel>PASSWORD</FieldLabel>
            <Box
              sx={{
                background: 'rgba(255,106,31,0.06)',
                border: `1px solid ${signalColors.accent}`,
                borderRadius: `${signalRadii.md}px`,
                padding: '11px 14px',
                boxShadow: '0 0 0 3px rgba(255,106,31,0.18)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Mono
                size={14}
                weight={600}
                color={signalText.primary}
                sx={{ letterSpacing: '0.2em', flex: 1 }}
              >
                ••••••••••
              </Mono>
              <Mono size={11} sx={{ cursor: 'pointer' }}>
                SHOW
              </Mono>
            </Box>

            {/* Four segments rather than a single bar — a discrete rating is
                easier to act on than a continuous one. */}
            <Box sx={{ display: 'flex', gap: '4px', marginTop: '3px' }}>
              {STRENGTH.map((filled, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    height: 3,
                    borderRadius: '999px',
                    background: filled
                      ? signalColors.green
                      : signalBorders.medium,
                  }}
                />
              ))}
            </Box>
            <Mono size={10} color={signalColors.green}>
              STRONG · 12+ CHARACTERS
            </Mono>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              marginTop: '4px',
            }}
          >
            <Box
              component="span"
              sx={{
                width: 17,
                height: 17,
                flex: 'none',
                borderRadius: '5px',
                background: signalColors.accent,
                color: signalColors.onAccent,
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '1px',
              }}
            >
              ✓
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: '12px',
                color: signalText.secondary,
                lineHeight: 1.5,
              }}
            >
              I agree to the <InlineLink>Terms</InlineLink> and{' '}
              <InlineLink>Privacy Policy</InlineLink>.
            </Box>
          </Box>

          <SignalButton
            size="xl"
            fullWidth
            sx={{
              padding: '12px 18px',
              borderRadius: `${signalRadii.md}px`,
              boxShadow: signalShadows.accentMd,
              marginTop: '4px',
            }}
          >
            Create account
          </SignalButton>
          <SignalButton
            variant="secondary"
            fullWidth
            sx={{
              padding: '11px 18px',
              borderRadius: `${signalRadii.md}px`,
              background: signalSurfaces.fillHover,
              borderColor: signalBorders.stronger,
              '&:hover': { background: 'rgba(255,255,255,0.11)' },
            }}
          >
            Continue with Google
          </SignalButton>
        </Box>

        <Box
          sx={{
            marginTop: 'auto',
            paddingTop: '24px',
            fontSize: '12.5px',
            color: signalText.muted,
          }}
        >
          Already have an account? <InlineLink>Sign in</InlineLink>
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen15Register;
